import React from "react"
import { AnsweredNode } from "../model/answered"
import { classes } from "./ScreenView.component"

interface Props {
  nodes: AnsweredNode[]
}

/** A level's children hang off a hairline guide. */
const GUIDE = "ml-1 border-l border-gray-200 pl-3"

/** A name and its answer: side by side, the answer on its own line when the two do not fit. */
const Row: React.FC<{ name: React.ReactNode; answer: string }> = ({ name, answer }) => (
  <div className="flex flex-wrap items-baseline gap-x-3">
    {name}
    {answer && <span className="ml-auto min-w-0 break-words text-right text-gray-900">{answer}</span>}
  </div>
)

/** A question answered: its title, its answer, and a grid's answered rows under it. */
const Question: React.FC<{ node: AnsweredNode }> = ({ node }) => (
  <li>
    <Row name={<span className="min-w-0 break-words text-gray-600">{node.title}</span>} answer={node.answer} />
    {node.children.length > 0 && (
      <ul className={classes("mt-1 space-y-1", GUIDE)}>
        {node.children.map((a) => (
          <li key={a.key}>
            <Row name={<span className="min-w-0 break-words text-gray-500">{a.title}</span>} answer={a.answer} />
          </li>
        ))}
      </ul>
    )}
  </li>
)

/** The Answered tab, read only: section, question, answer, each answer as the card showed it. */
const AnsweredTree: React.FC<Props> = ({ nodes }) =>
  nodes.length ? (
    <ul className="space-y-2 text-sm [overflow-wrap:anywhere]">
      {nodes.map((n) =>
        n.kind === "section" ? (
          <li key={n.key}>
            <div className="break-words text-xs font-medium text-gray-500">{n.title}</div>
            <ul className={classes("mt-1 space-y-1.5", GUIDE)}>
              {n.children.map((q) => (
                <Question key={q.key} node={q} />
              ))}
            </ul>
          </li>
        ) : (
          <Question key={n.key} node={n} />
        )
      )}
    </ul>
  ) : (
    <div className="text-sm text-gray-500">No answers yet.</div>
  )

export default AnsweredTree
